import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ScorecardNavigation from './ScorecardNavigation';
import ShiftMetricsTable, { type ShiftMetricsRow } from './ShiftMetricsTable';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { config } from '../../config/environment';
import {
  getStoreShifts,
  type StoreShiftRow,
  type StoreShiftsGroup,
  type StoreShiftsResponse,
  type MetricColumn,
  ServerScorecardForbiddenError,
} from '../../services/serverScorecardService';
import { RoleBand } from './RoleGridHeading';
import { groupsOf, visibleGroups, showRoleHeadings } from '../../services/scorecardGroups';

// feat-317: an old backend (no `groups`) → one legacy group from the top level.
const toGroups = (resp: StoreShiftsResponse): StoreShiftsGroup[] =>
  groupsOf(resp, (r): StoreShiftsGroup => ({
    role: null,
    display_name: null,
    metric_columns: r.metric_columns || [],
    drink_column: r.drink_column || null,
    rows: r.rows || [],
  }));

interface ManagerShiftDetailsProps {
  date?: string;
  shift?: string;
  score?: number;
  managerName?: string;
  shiftId?: string;
  managerId?: string;
}

const ManagerShiftDetails: React.FC<ManagerShiftDetailsProps> = ({
  date = 'September 29, 2025',
  shift = 'Active',
  score = 0,
  managerName = 'Matt Johnson',
  shiftId = '',
  managerId = ''
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { metricLabels, selectedTenant } = useTenant();

  // Get shift data from location state or use defaults
  const shiftData = location.state?.shiftData || { date, shift, score, managerName, shiftId, managerId };

  // feat-093 Slice C: per-server rows for THIS manager shift come from the
  // backend GET /api/server-scorecard/{tenant}/shifts?shift_id=<managerShiftId>
  // endpoint. Score, visit_count and per-metric percents are all backend-computed
  // and rendered VERBATIM — the old client-side metric recompute + raw-SQL/Revel
  // fetches are gone (feedback_metrics_single_source; AC-C3/C4).
  //
  // feat-317 (ADR §7.3): one group per effective role, each with its own
  // columns and rows, rendered in response order.
  const [groups, setGroups] = useState<StoreShiftsGroup[]>([]);
  const clearRows = () => setGroups((prev) => prev.map((g) => ({ ...g, rows: [] })));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resolve the tenant subject for the RBAC'd endpoint (own tenant; foreign
  // tenant fails closed server-side).
  const tenantId = selectedTenant || user?.managementUser?.tenant_id || undefined;

  useEffect(() => {
    const fetchShiftRows = async () => {
      if (config.useDummyData) {
        // Fake mode: the service returns canned rows; filter by the drill-in id.
        const resp = await getStoreShifts('fake-tenant', { shiftId: shiftData.shiftId || undefined });
        setGroups(toGroups(resp));
        return;
      }
      if (!shiftData.shiftId || !tenantId) {
        clearRows();
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const resp = await getStoreShifts(tenantId, { shiftId: shiftData.shiftId });
        setGroups(toGroups(resp));
      } catch (err) {
        if (err instanceof ServerScorecardForbiddenError) {
          clearRows();
        } else {
          console.error('Error fetching server contributions:', err);
          setError(err instanceof Error ? err.message : 'Failed to fetch server contributions');
          clearRows();
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchShiftRows();
  }, [shiftData.shiftId, tenantId]);

  const handleBackToManagerScorecard = () => {
    // Restore the filter state that was saved when navigating to details
    const savedFilters = location.state?.filters;
    navigate('/restaurant/manager-scorecard', {
      state: {
        filters: savedFilters || {}
      }
    });
  };

  // feat-317: every role group's rows. The Shift Score and Servers Contributing
  // tiles span ALL of them — one number across roles (spec round-3 Q1).
  const storeShiftRows: StoreShiftRow[] = useMemo(() => groups.flatMap((g) => g.rows), [groups]);
  // Groups with rows, in response order; all empty → the first, emptied.
  const tableGroups = useMemo(() => visibleGroups(groups, (g) => g.rows), [groups]);

  // Display-average of the BACKEND per-shift scores (averaging backend numbers,
  // not re-deriving a rate — allowed under single-source).
  const averageServerScore = useMemo(() => {
    const scored = storeShiftRows.filter((r) => r.score !== null && r.score !== undefined);
    if (scored.length === 0) return 0;
    const totalScore = scored.reduce((sum, r) => sum + (r.score as number), 0);
    return Math.round(totalScore / scored.length);
  }, [storeShiftRows]);

  // Build render rows from backend values — every numeric field is verbatim.
  // feat-317: per group, against that group's own columns.
  const buildTableRows = (group: StoreShiftsGroup | undefined): ShiftMetricsRow[] => {
    const metricColumns: MetricColumn[] = group?.metric_columns || [];
    const drinkColumn: MetricColumn | null = group?.drink_column || null;
    return (group?.rows || []).map((row) => {
      const metricValues: Record<string, number> = {};
      for (const col of metricColumns) {
        const cell = row.metrics[col.key];
        metricValues[col.key] =
          cell && cell.percent !== null && cell.percent !== undefined ? cell.percent : 0;
      }
      if (drinkColumn) {
        metricValues[drinkColumn.key] =
          row.drink && row.drink.percent !== null && row.drink.percent !== undefined
            ? row.drink.percent
            : 0;
      }
      return {
        serverName: row.server_name,
        type: 'all',
        greetings: row.greetings ?? 0,
        visitCount: row.visit_count ?? row.greetings ?? undefined,
        score: row.score ?? 0,
        premiumMenu: 0,
        itemMention: 0,
        itemDescription: 0,
        drinkPromotions: 0,
        metricValues,
      };
    });
  };

  const renderTable = (group: StoreShiftsGroup | undefined) => (
    <ShiftMetricsTable
      shifts={buildTableRows(group)}
      showServer={true}
      showShift={false}
      showGreetings={true}
      metricLabels={metricLabels}
      dynamicMetrics={(group?.metric_columns || []).map((c) => ({ key: c.key, label: c.label } as any))}
      dynamicDrinkLabel={group?.drink_column ? group.drink_column.label : null}
      emptyMessage="No servers found for this shift."
    />
  );

  const serverCount = storeShiftRows.length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <ScorecardNavigation />

      <div className="p-6">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Manager Shift Details</h1>
            <p className="text-lg text-gray-600 mt-2">
              {shiftData.managerName} • {shiftData.date} • {shiftData.shift}
            </p>
          </div>
          <button
            onClick={handleBackToManagerScorecard}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            ← Back to Manager Scorecard
          </button>
        </div>

        {/* Shift Score Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
              Shift Score
            </div>
            <div className="text-3xl font-bold text-gray-900">
              {averageServerScore}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
              Servers Contributing
            </div>
            <div className="text-3xl font-bold text-gray-900">
              {serverCount}
            </div>
          </div>
        </div>

        {/* Server Contributions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Servers Contributing to the Shift Score</h2>
          </div>
          
          {isLoading ? (
            <div className="p-6 text-center">
              <div className="inline-flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading server contributions...
              </div>
            </div>
          ) : error ? (
            <div className="p-6 text-center text-red-600">
              <p>Error: {error}</p>
            </div>
          ) : showRoleHeadings(tableGroups) ? (
            // feat-317 (design NOTES §4.4): a role band + table per group.
            <div>
              {tableGroups.map((g, i) => (
                <div key={g.role ?? `group-${i}`}>
                  <RoleBand displayName={g.display_name} role={g.role} first={i === 0} />
                  {renderTable(g)}
                </div>
              ))}
            </div>
          ) : (
            renderTable(tableGroups[0])
          )}
        </div>
      </div>
    </div>
  );
};

export default ManagerShiftDetails;
